import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { PaymentSuccessFooter } from '../components/Payment';
import { Environment } from '../constants/environments';
import { HttpVerb } from '../constants/httpVerbs';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useLoaderScreenContext } from '../hooks/contexts/loader-screen.context';
import { useResaleUserContext } from '../hooks/contexts/resale-user.context';
import { useResaleContext } from '../hooks/contexts/resale.context';
import { useResaleEventContext } from '../hooks/contexts/resaleEvent.context';
import { useApiService } from '../hooks/useApiService';
import { useResaleTracking } from '../hooks/useResaleTracking';
import { useFastPassOfferContext } from '../providers/FastPassOfferProvider';
import * as Styles from '../styles/pages/payment';
import { trackResalePurchase } from '../trackings/analyticsDataLayer';
import { ResaleData } from '../types/resale';
import {
  ResaleShopErrorCode,
  ResaleShopErrorResponse,
  ResaleShopRequest,
  ResaleShopResponse
} from '../types/resaleShop';
import { CircuitBreaker } from '../utils/CircuitBreaker';
import getIngCookieValue from '../utils/getIngCookies';
import { logger } from '../utils/logger';
import { RateLimiter } from '../utils/RateLimiter';
import { waitForIframeSrc } from '../utils/waitForIframe';

type ResalePaymentParams = {
  resaleId: string;
};

// Global circuit breaker for resale checkout API calls
// Prevents cascading failures when API is down
const resaleCheckoutCircuitBreaker = new CircuitBreaker({
  name : 'ResaleCheckout',
  failureThreshold : 5,     // Open circuit after 5 failures
  resetTimeout : 60000      // Try again after 1 minute
});

// Global rate limiter for resale checkout requests
// Prevents abuse via DevTools spam
const resaleCheckoutRateLimiter = new RateLimiter({
  name : 'ResaleCheckout',
  maxRequests : 3,          // Max 3 requests
  windowMs : 60000          // Per 1 minute window
});

/**
 * Dedicated payment page for resale ticket purchases
 *
 * Separated from regular payment flow for:
 * - Clear separation of concerns
 * - Simplified logic (single ticket, no combos)
 * - Different analytics tracking
 * - No dependency on CartContext
 *
 * Follows Clean Architecture principles:
 * - Single Responsibility
 * - DRY via component reuse
 * - No side effects on existing payment flow
 */
const ResalePaymentPage: React.FC = () => {
  const { resaleId } = useParams<ResalePaymentParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const { source : waitlistSource, waitlistId } = (location.state ?? {}) as {
    source?: string;
    waitlistId?: string;
  };
  const posthog = usePostHog();

  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [checkoutPaid, setCheckoutPaid] = useState(false);
  const [transactionId, setTransactionId] = useState<string>('');
  const [iframeError, setIframeError] = useState(false);

  // Prevent multiple simultaneous calls to POST /shop-resale
  const isRequestingCheckout = useRef(false);
  // Prevent multiple iframe mounts
  const isMounted = useRef(false);

  const { resaleData, loading, getResaleById } = useResaleContext();
  const { event, fetchEventById } = useResaleEventContext();
  const { user } = useResaleUserContext();
  const { toggleLoader } = useLoaderScreenContext();
  const {
    currentLocale : { locale },
    translate
  } = useI18nContext();
  const { request } = useApiService();
  const { paymentTracking } = useResaleTracking();
  const {
    offerId,
    finalPrice,
    isValid : isOfferValid,
    loading : offerLoading
  } = useFastPassOfferContext();
  const isOfferResolving = !!offerId && offerLoading;

  /**
   * MKT-490 — analytics resale purchase (GA4/Meta via GTM + CAPI), deduped by
   * transaction_id so it fires once even if the confirmation page also runs.
   *
   * Shared by the two success paths (instant approval and the iframe `onSuccess`)
   * so the taxonomy mapping lives in ONE place — the resale flow is not covered by
   * `itemTaxonomy.test.ts`, so a divergence between the two would go unnoticed.
   *
   * Value = FastPass final price when the offer is valid, else the resale sale
   * value, keeping GA4 revenue aligned with what the buyer actually paid.
   */
  const trackResale = (txId: string) => {
    if (!resaleData || !user) return;

    const paidValue = isOfferValid && finalPrice !== null ? finalPrice : resaleData.saleValue;

    trackResalePurchase({
      eventId : resaleData.event.id,
      eventTitle : resaleData.event.title,
      producerId : resaleData.event.producerId,
      ticketVariant : resaleData.ticket.guestType?.name,
      transactionId : txId,
      value : paidValue,
      currency : 'BRL',
      user : { id : user.id, email : user.email, name : user.name },
      affiliation : 'resale',
    });
  };

  /**
   * Track page view when data is ready
   */
  useEffect(() => {
    if (resaleData && event && user) {
      paymentTracking.trackPageView(resaleData, event, user, true);
      logger.info('[ResalePayment] Tracked page view');
    }
  }, [resaleData, event, user, paymentTracking]);

  /**
   * Load resale data on mount
   */
  useEffect(() => {
    if (resaleId) {
      getResaleById(resaleId);
    } else {
      logger.warn('[ResalePayment] No resaleId found in URL params');
    }
    // Note: getResaleById omitted from deps to prevent infinite loop (unstable context function)
  }, [resaleId]);

  /**
   * Fetch event data when resale data is available
   */
  useEffect(() => {
    if (resaleData?.event?.id) {
      fetchEventById(resaleData.event.id);
    }
    // Note: fetchEventById omitted from deps to prevent infinite loop (unstable context function)
  }, [resaleData?.event?.id]);

  /**
   * Request checkout URL from payment gateway
   * Uses POST /shop-resale endpoint with simplified payload
   * @see RESALE_SANDBOX_TESTS.md - TESTE 2
   */
  const requestCheckoutUrl = useCallback(async () => {
    if (!resaleData || !user) {
      logger.error('Missing resale data or user');
      return;
    }

    // Block seller from buying their own resale (second guard, before API call)
    if (Number(user.id) === Number(resaleData.userId)) {
      navigate(`/resale/${resaleId}`, { state : { errorType : 'ownResale' } });
      return;
    }

    // Rate limiting check - prevent spam/abuse
    if (!resaleCheckoutRateLimiter.canProceed()) {
      const timeUntilReset = Math.ceil(resaleCheckoutRateLimiter.getTimeUntilReset() / 1000);
      const errorMsg = `Muitas tentativas. Por favor, aguarde ${timeUntilReset} segundos.`;

      logger.warn('[ResalePayment] Rate limit exceeded', { timeUntilReset });

      // Track rate limit error
      if (event) {
        paymentTracking.trackCheckoutRequestFailed(resaleData, event, user, errorMsg);
      }

      navigate(`/resale/${resaleId}`);
      return;
    }

    // Set lock flag to prevent concurrent requests
    isRequestingCheckout.current = true;

    try {
      toggleLoader({ show : true });

      // Validate with fresh data before proceeding — prevents POST /shop-resale
      // from firing with stale context data from a previous navigation.
      const apiKey = Environment.API_KEY;
      const freshResponse = await request<null, {
        responseData: ResaleData;
        responseError: string | null;
      }>(
        { requestOptions : { resourcePath : `/resale/${resaleId}?apikey=${apiKey}` }, type : HttpVerb.GET },
        { baseUrl : Environment.API_URL }
      );

      const freshData = freshResponse?.responseData;

      if (!freshData?.isActive || freshData.isSold || freshData.isDeactivated) {
        navigate(`/resale/${resaleId}`);
        return;
      }

      if (Number(user.id) === Number(freshData.userId)) {
        navigate(`/resale/${resaleId}`, { state : { errorType : 'ownResale' } });
        return;
      }

      // Track checkout request started
      if (event) {
        paymentTracking.trackCheckoutRequestStarted(
          resaleData,
          event,
          user,
          isOfferValid && offerId && finalPrice !== null
            ? { offerId, spreadCents : Math.round((finalPrice - resaleData.saleValue) * 100) }
            : undefined
        );
      }

      // Build resource path with API key and user token
      const userToken = getIngCookieValue('token');
      const resourcePath = `shop-resale?apikey=${apiKey}&usertoken=${userToken}`;

      // Validate required fields before building payload
      if (!user.document?.number) {
        logger.error('[ResalePayment] Missing user document number');
        navigate(`/resale/${resaleId}`);
        return;
      }

      // Prepare simplified payload for resale purchase
      const payload: ResaleShopRequest = {
        userId : user.id,
        resaleId : resaleData.uuid,
        document : user.document.number,
        ...(offerId ? { offerId } : {}),
        ...(waitlistId ? { source : waitlistSource, waitlistId } : {}),
      };

      // Wrap API call with circuit breaker for resilience
      // NOTE: Retry logic removed due to lack of idempotency in backend
      // to prevent duplicate transactions
      const response = await resaleCheckoutCircuitBreaker.execute(async () => {
        // Call resale shop endpoint with dual authentication
        const result = await request<ResaleShopRequest, ResaleShopResponse>(
          {
            requestOptions : {
              resourcePath,
              data : payload
            },
            type : HttpVerb.POST
          },
          {
            baseUrl : Environment.API_GATEWAY_URL,
            needToken : true // Adds Authorization: Bearer header
          }
        );

        // Validate response
        if (!result?.responseData) {
          throw new Error('Invalid response from payment gateway');
        }

        return result;
      });

      const { checkoutUrl, transactionId } = response.responseData;

      // Track checkout request success
      if (event) {
        paymentTracking.trackCheckoutRequestSuccess(
          resaleData,
          event,
          user,
          transactionId,
          undefined,
          isOfferValid && offerId && finalPrice !== null
            ? { offerId, spreadCents : Math.round((finalPrice - resaleData.saleValue) * 100) }
            : undefined
        );
      }

      // Handle immediate approval
      if (checkoutUrl === 'approved') {
        // Track payment completed for immediate approval
        if (event) {
          paymentTracking.trackPaymentCompleted(
            resaleData,
            event,
            user,
            transactionId,
            undefined,
            isOfferValid && offerId && finalPrice !== null
              ? { offerId, spreadCents : Math.round((finalPrice - resaleData.saleValue) * 100) }
              : undefined
          );

          trackResale(transactionId);
        }

        navigate(
          `/payment-confirmed/ingresse.com/event/${resaleData.event.id}/`
        );
        return;
      }

      // Set iframe URL and transaction ID
      setIframeUrl(checkoutUrl);
      setTransactionId(transactionId);

      // Persist to sessionStorage to recover checkout state on F5
      sessionStorage.setItem(`alchemist_resale_checkout_url_${resaleId}`, checkoutUrl);
      sessionStorage.setItem(`alchemist_resale_checkout_txid_${resaleId}`, transactionId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      logger.error('[ResalePayment] Failed to request checkout URL', {
        error : errorMessage
      });

      // Track checkout request failed
      if (event) {
        paymentTracking.trackCheckoutRequestFailed(
          resaleData,
          event,
          user,
          errorMessage
        );
      }

      // Handle UNAUTHORIZED — session expired, redirect to login
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: ResaleShopErrorResponse };
        };
        const errorCode = axiosError.response?.data?.detail?.code;

        if (errorCode === ResaleShopErrorCode.UNAUTHORIZED) {
          window.location.href = '/';
          return;
        }
      }

      navigate(`/resale/${resaleId}`);
    } finally {
      toggleLoader({ show : false });
      // Release lock flag to allow future requests
      isRequestingCheckout.current = false;
    }
  }, [
    resaleData,
    user,
    toggleLoader,
    request,
    navigate,
    resaleId,
    offerId,
    isOfferValid
  ]);

  // Keep ref to latest version of requestCheckoutUrl to avoid race conditions
  const requestCheckoutUrlRef = useRef(requestCheckoutUrl);

  useEffect(() => {
    requestCheckoutUrlRef.current = requestCheckoutUrl;
  });

  /**
   * Handle payment success
   * Memoized to prevent recreating on every render and causing useEffect loops
   */
  const handleSuccess = useCallback(
    (details: IPaymentDetails) => {
      const { status } = details;

      logger.info('Resale payment success:', {
        status,
        resaleId,
        transactionId
      });

      // MKT-490 — bespoke GA4 Purchase tracking removed in the GTM cutover. The
      // resale-specific `paymentTracking` (PostHog) below stays as-is.
      switch (status) {
      case 'paid':
      case 'authorized':
      case 'partially paid':
        // Track payment completed
        if (resaleData && event && user) {
          paymentTracking.trackPaymentCompleted(
            resaleData,
            event,
            user,
            transactionId,
            undefined,
            isOfferValid && offerId && finalPrice !== null
              ? { offerId, spreadCents : Math.round((finalPrice - resaleData.saleValue) * 100) }
              : undefined
          );

          trackResale(transactionId);
        }
        // Clear checkout session — payment completed, no longer needed
        sessionStorage.removeItem(`alchemist_resale_checkout_url_${resaleId}`);
        sessionStorage.removeItem(`alchemist_resale_checkout_txid_${resaleId}`);
        setCheckoutPaid(true);
        break;

      case 'pending':
        navigate(
          `/payment-in-process/ingresse.com/event/${resaleData?.event.id}/`
        );
        break;

      default:
        // Track payment failed
        if (resaleData && event && user) {
          paymentTracking.trackPaymentFailed(
            resaleData,
            event,
            user,
            `Payment cancelled or failed with status: ${status}`,
            status,
            transactionId
          );
        }
        logger.warn('Payment cancelled or failed', { status });
        navigate(`/resale/${resaleId}`);
        break;
      }
    },
    [resaleId, transactionId, resaleData, navigate, isOfferValid, finalPrice, event, user, paymentTracking]
  );

  // Keep refs to stable callbacks for iframe mount to prevent remount
  const handleSuccessRef = useRef(handleSuccess);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    handleSuccessRef.current = handleSuccess;
    navigateRef.current = navigate;
  });

  /**
   * Navigation handlers with analytics tracking
   */
  const redirectToSiteUrl = () => {
    posthog?.capture('resale_purchase:success_page_show_events_click', {
      resaleId,
      transactionId
    });
    window.location.href = Environment.SITE_URL;
  };

  const redirectToWallet = () => {
    posthog?.capture('resale_purchase:success_page_show_wallet_click', {
      resaleId,
      transactionId
    });
    window.location.href = `${Environment.WALLET_URL}/?origin=shop`;
  };

  /**
   * Initialize checkout on mount
   */
  useEffect(() => {
    if (!resaleData || !user) {
      // Wait for data to load
      if (!loading) {
        // Track payment session expired
        if (resaleId) {
          paymentTracking.trackPaymentSessionExpired(
            resaleId,
            'Payment session expired or invalid - no resale data or user available'
          );
        }

        logger.error('[ResalePayment] REDIRECTING - No resale data or user', {
          hasResaleData : !!resaleData,
          hasUser : !!user,
          loading
        });
        navigate(`/resale/${resaleId}`);
      }
      return;
    }

    // Wait for data refresh to complete before proceeding
    if (loading) {
      return;
    }

    // Wait for FastPass offer resolution before proceeding
    if (isOfferResolving) {
      return;
    }

    // Validate availability with fresh data before opening transaction
    if (!resaleData.isActive || resaleData.isSold || resaleData.isDeactivated) {
      navigate(`/resale/${resaleId}`);
      return;
    }

    // Block seller from buying their own resale
    if (Number(user.id) === Number(resaleData.userId)) {
      navigate(`/resale/${resaleId}`, { state : { errorType : 'ownResale' } });
      return;
    }

    // Skip if already have iframe URL or if already requesting
    if (iframeUrl || isRequestingCheckout.current) {
      return;
    }

    // F5 recovery: restore existing checkout session to avoid duplicate transactions
    const savedUrl   = sessionStorage.getItem(`alchemist_resale_checkout_url_${resaleId}`);
    const savedTxId  = sessionStorage.getItem(`alchemist_resale_checkout_txid_${resaleId}`);
    if (savedUrl && savedTxId) {
      setIframeUrl(savedUrl);
      setTransactionId(savedTxId);
      return;
    }

    // Clear any existing transaction
    setTransactionId('');

    // Request checkout URL (use ref to avoid race conditions)
    requestCheckoutUrlRef.current();
  }, [
    resaleData,
    user,
    iframeUrl,
    loading,
    resaleId,
    navigate,
    isOfferResolving
  ]);

  /**
   * Mount payment iframe when URL is ready
   */
  useEffect(() => {
    if (!iframeUrl) {
      return;
    }

    // Prevent duplicate iframe mounting
    if (isMounted.current) {
      logger.warn(
        '[ResalePayment] Iframe already mounted, skipping duplicate mount'
      );
      return;
    }

    if (!window.IngresseCheckout) {
      logger.error('[ResalePayment] IngresseCheckout not available');
      setIframeError(true);
      return;
    }

    const localeLower = locale.toLowerCase().replace('-', '_');

    // Mount iframe
    const mountStartTime = Date.now();

    try {
      window.IngresseCheckout.mount('checkout-frame', iframeUrl, {
        locale : localeLower,
        onSuccess : (details) => handleSuccessRef.current(details),
        onTimeout : () => {
          logger.warn('Resale payment timeout');
          navigateRef.current(`/resale/${resaleId}`);
        }
        // Note: style object removed to prevent overriding iframe internal styles
        // The iframe should use its own default styling
      });

      // Mark as mounted
      isMounted.current = true;

      // Track iframe mounted
      const mountTime = Date.now() - mountStartTime;
      if (resaleData && event && user) {
        paymentTracking.trackIframeMounted(
          resaleData,
          event,
          user,
          transactionId,
          mountTime
        );
      }

      // Verify iframe src was set correctly with robust polling
      // Replaces fixed setTimeout with adaptive polling for slow devices
      waitForIframeSrc({
        selector : '#checkout-frame iframe',
        expectedSrc : iframeUrl,
        timeoutMs : 5000,
        pollIntervalMs : 50
      }).catch(error => {
        logger.error('[ResalePayment] Failed to verify iframe src', { error });
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      logger.error('[ResalePayment] Failed to mount iframe', {
        error : errorMessage
      });

      // Track iframe error
      if (resaleData && event && user) {
        paymentTracking.trackIframeError(
          resaleData,
          event,
          user,
          errorMessage,
          transactionId
        );
      }

      isMounted.current = false;
      setIframeError(true);
    }

    // Cleanup on unmount
    return () => {
      // Cancel checkout session
      if (window.IngresseCheckout) {
        window.IngresseCheckout.cancel();
      }

      // Remove iframe from DOM explicitly
      const container = document.querySelector('#checkout-frame');
      if (container) {
        container.innerHTML = '';
      }

      // Reset mounted flag
      isMounted.current = false;
    };
  }, [iframeUrl, locale, resaleId]);

  // Show error UI if payment iframe failed to load
  if (iframeError) {
    return (
      <Styles.WrapperPayment id='resale-payment-page'>
        <Styles.IframeErrorContainer>
          <Styles.IframeErrorText>
            {translate(['RESALE', 'PAYMENT_IFRAME_ERROR'])}
          </Styles.IframeErrorText>
          <Styles.CustomBigButton
            type='button'
            onClick={() => navigate(`/resale/${resaleId}`)}
          >
            {translate(['RESALE', 'TRY_AGAIN'])}
          </Styles.CustomBigButton>
        </Styles.IframeErrorContainer>
      </Styles.WrapperPayment>
    );
  }

  // Show nothing until iframe is ready
  if (!iframeUrl) {
    return null;
  }

  return (
    <Styles.WrapperPayment id='resale-payment-page'>
      <Styles.CheckoutFrame id='checkout-frame' />

      {!checkoutPaid && (
        <Styles.ResaleDisclaimer id='resale-disclaimer'>
          {translate(['RESALE', 'CANCELLATION_DISCLAIMER'])}
        </Styles.ResaleDisclaimer>
      )}

      {checkoutPaid && (
        <PaymentSuccessFooter
          onSeeMoreEvents={redirectToSiteUrl}
          onViewMyTickets={redirectToWallet}
          seeMoreEventsLabel={translate(['INFORMATION', 'SEE_MORE_EVENTS'])}
          myTicketsLabel={translate(['PAYMENT_STATUS.TEXT', 'PT_50'])}
        />
      )}
    </Styles.WrapperPayment>
  );
};

export default ResalePaymentPage;
